import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { LineChart } from '../../components/charts/LineChart.js'
import { Metric } from '../../components/Metric.js'
import { useAuth } from '../../lib/auth.js'
import { formatCount, formatDecimal, formatRate } from '../../lib/format.js'
import { Chip, Heading, Text } from '../../ui/index.js'

type InsightBucket = 'week' | 'month'

interface InsightParams {
  readonly from: string
  readonly to: string
  readonly departmentId: string | null
  readonly bucket: InsightBucket
}

interface ThroughputPoint {
  readonly from: string
  readonly to: string
  readonly closed: number
  readonly complexity: number
}

interface ThroughputResponse {
  readonly points: readonly ThroughputPoint[]
}

interface CycleTimeResponse {
  readonly assignedToStartedHours: number | null
  readonly startedToSubmittedHours: number | null
  readonly submittedToClosedHours: number | null
  readonly sample: number
}

interface ReviewLatencyResponse {
  readonly medianHours: number | null
  readonly p90Hours: number | null
  readonly unreviewed: number
  readonly oldestUnreviewedHours: number | null
}

interface WorkloadPerson {
  readonly userId: string
  readonly name: string
  readonly activeTasks: number
  readonly openComplexity: number
  readonly overloaded: boolean
}

interface WorkloadResponse {
  readonly people: readonly WorkloadPerson[]
  readonly medianOpenComplexity: number
}

interface DeadlineRealismResponse {
  readonly extensions: number
  readonly tasksWithExtension: number
  readonly closedTasks: number
  readonly extendedShare: number
}

interface ReworkCategory {
  readonly category: string
  readonly returnedComplexity: number
  readonly totalComplexity: number
  readonly rate: number
}

interface ReworkResponse {
  readonly categories: readonly ReworkCategory[]
}

interface LeaveAdjustedPerson {
  readonly userId: string
  readonly name: string
  readonly closedTasks: number
  readonly leaveDays: number
  readonly availableDays: number
  readonly closedPerAvailableDay: number | null
}

interface LeaveAdjustedResponse {
  readonly people: readonly LeaveAdjustedPerson[]
}

const cardStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--o-space-4)',
  display: 'grid',
  gap: 'var(--o-space-3)',
} as const

const headerStyle = {
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'space-between',
  gap: 'var(--o-space-3)',
} as const

function queryPath(base: string, params: InsightParams): string {
  const query = new URLSearchParams({ from: params.from, to: params.to })
  if (params.departmentId !== null) query.set('departmentId', params.departmentId)
  if (base === '/api/analytics/throughput') query.set('bucket', params.bucket)
  return `${base}?${query.toString()}`
}

function formatHours(value: number | null): string {
  return value === null ? '—' : `${formatDecimal(value)}h`
}

function useInsightQuery<T>(queryKey: readonly unknown[], path: string) {
  const { api } = useAuth()
  return useQuery({
    queryKey,
    retry: false,
    queryFn: () => api.get<T>(path),
  })
}

function PanelShell({
  eyebrow,
  title,
  children,
  note,
}: {
  readonly eyebrow: string
  readonly title: string
  readonly children: ReactNode
  readonly note?: ReactNode
}) {
  return (
    <section style={cardStyle}>
      <div style={headerStyle}>
        <div>
          <div className="card-label">{eyebrow}</div>
          <Heading type="h3" style={{ margin: 0 }}>
            {title}
          </Heading>
        </div>
      </div>
      {note === undefined ? null : <Text color="secondary">{note}</Text>}
      {children}
    </section>
  )
}

export function ThroughputPanel(params: InsightParams) {
  const query = useInsightQuery<ThroughputResponse>(
    ['insights-throughput', params.from, params.to, params.departmentId, params.bucket],
    queryPath('/api/analytics/throughput', params),
  )
  if (query.isPending) return <LoadingState label="Loading throughput" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const points = query.data?.points ?? []
  const totalClosed = points.reduce((sum, point) => sum + point.closed, 0)
  const totalComplexity = points.reduce((sum, point) => sum + point.complexity, 0)
  const yMax = Math.max(1, ...points.map((point) => point.closed))
  return (
    <PanelShell eyebrow="Throughput" title="Closed work over time">
      <div className="metric-grid metric-grid--compact">
        <Metric compact label="Closed tasks" value={formatCount(totalClosed)} footnote="in range" />
        <Metric compact label="Complexity" value={formatDecimal(totalComplexity)} footnote="closed" />
      </div>
      {points.length === 0 ? (
        <EmptyState title="No throughput data" hint="Pick a period with closed work to see the chart." />
      ) : (
        <LineChart
          width={520}
          height={180}
          yMax={yMax}
          xLabels={points.map((point) => point.from.slice(5, 10))}
          series={[{ label: 'Closed', data: points.map((point) => point.closed), color: 'var(--brand)', fill: true, dots: true }]}
        />
      )}
    </PanelShell>
  )
}

export function CycleTimePanel(params: InsightParams) {
  const query = useInsightQuery<CycleTimeResponse>(
    ['insights-cycle-time', params.from, params.to, params.departmentId],
    queryPath('/api/analytics/cycle-time', params),
  )
  if (query.isPending) return <LoadingState label="Loading cycle time" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const data = query.data
  return (
    <PanelShell eyebrow="Cycle time" title="Time between task stages">
      <div className="metric-grid metric-grid--compact">
        <Metric compact label="Assigned to started" value={formatHours(data?.assignedToStartedHours ?? null)} footnote="median hours" />
        <Metric compact label="Started to submitted" value={formatHours(data?.startedToSubmittedHours ?? null)} footnote="median hours" />
        <Metric compact label="Submitted to closed" value={formatHours(data?.submittedToClosedHours ?? null)} footnote="median hours" />
      </div>
      <Text color="secondary">Sample size: {formatCount(data?.sample ?? 0)} tasks.</Text>
    </PanelShell>
  )
}

export function ReviewLatencyPanel(params: InsightParams) {
  const query = useInsightQuery<ReviewLatencyResponse>(
    ['insights-review-latency', params.from, params.to, params.departmentId],
    queryPath('/api/analytics/review-latency', params),
  )
  if (query.isPending) return <LoadingState label="Loading review latency" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const data = query.data
  return (
    <PanelShell
      eyebrow="Review latency"
      title="How fast submissions get reviewed"
      note="How quickly submissions get reviewed. This one measures the manager."
    >
      <div className="metric-grid metric-grid--compact">
        <Metric compact label="Median" value={formatHours(data?.medianHours ?? null)} footnote="turnaround" />
        <Metric compact label="P90" value={formatHours(data?.p90Hours ?? null)} footnote="slowest" />
        <Metric compact label="Unreviewed" value={formatCount(data?.unreviewed ?? 0)} footnote="open" />
        <Metric compact label="Oldest open" value={formatHours(data?.oldestUnreviewedHours ?? null)} footnote="since submit" />
      </div>
    </PanelShell>
  )
}

export function WorkloadPanel(params: InsightParams) {
  const query = useInsightQuery<WorkloadResponse>(
    ['insights-workload', params.from, params.to, params.departmentId],
    queryPath('/api/analytics/workload', params),
  )
  if (query.isPending) return <LoadingState label="Loading workload" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const people = query.data?.people ?? []
  return (
    <PanelShell eyebrow="Workload" title="Active load by person">
      <Text color="secondary">Median open complexity: {formatDecimal(query.data?.medianOpenComplexity ?? 0)}.</Text>
      {people.length === 0 ? (
        <EmptyState title="No active workload" hint="No one has open tasks in this range." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
          {people.map((person) => (
            <div
              key={person.userId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--o-space-3)',
                padding: 'var(--o-space-3)',
                borderRadius: 'var(--r-md)',
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border)',
              }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>{person.name}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  {formatCount(person.activeTasks)} active tasks · complexity {formatDecimal(person.openComplexity)}
                </div>
              </div>
              <Chip label={person.overloaded ? 'Overloaded' : 'Balanced'} color={person.overloaded ? 'warning' : 'positive'} filled />
            </div>
          ))}
        </div>
      )}
    </PanelShell>
  )
}

export function DeadlineRealismPanel(params: InsightParams) {
  const query = useInsightQuery<DeadlineRealismResponse>(
    ['insights-deadline-realism', params.from, params.to, params.departmentId],
    queryPath('/api/analytics/deadline-realism', params),
  )
  if (query.isPending) return <LoadingState label="Loading deadline realism" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const data = query.data
  return (
    <PanelShell eyebrow="Deadline realism" title="How often deadlines move">
      <div className="metric-grid metric-grid--compact">
        <Metric compact label="Extensions" value={formatCount(data?.extensions ?? 0)} footnote="deadline adjustments" />
        <Metric compact label="Tasks extended" value={formatCount(data?.tasksWithExtension ?? 0)} footnote="closed tasks with a move" />
        <Metric compact label="Extended share" value={formatRate(data?.extendedShare ?? 0)} footnote="extensions per closed task" />
      </div>
      <Text color="secondary">Closed tasks: {formatCount(data?.closedTasks ?? 0)}.</Text>
    </PanelShell>
  )
}

export function ReworkPanel(params: InsightParams) {
  const query = useInsightQuery<ReworkResponse>(
    ['insights-rework', params.from, params.to, params.departmentId],
    queryPath('/api/analytics/rework', params),
  )
  if (query.isPending) return <LoadingState label="Loading rework" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const categories = query.data?.categories ?? []
  return (
    <PanelShell eyebrow="Rework" title="Returned work by category">
      {categories.length === 0 ? (
        <EmptyState title="No rework data" hint="Closed work with returns will appear here." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Category</th>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Returned complexity</th>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Total complexity</th>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Rate</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((row) => (
                <tr key={row.category} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>{row.category}</td>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>{formatDecimal(row.returnedComplexity)}</td>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>{formatDecimal(row.totalComplexity)}</td>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>{formatRate(row.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PanelShell>
  )
}

export function LeaveAdjustedPanel(params: InsightParams) {
  const query = useInsightQuery<LeaveAdjustedResponse>(
    ['insights-leave-adjusted', params.from, params.to, params.departmentId],
    queryPath('/api/analytics/leave-adjusted', params),
  )
  if (query.isPending) return <LoadingState label="Loading leave-adjusted throughput" />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  const people = query.data?.people ?? []
  return (
    <PanelShell
      eyebrow="Leave adjusted"
      title="Leave-adjusted throughput"
      note="Approved leave is removed from the denominator so the output reflects available time."
    >
      <div className="metric-grid metric-grid--compact">
        <Metric compact label="People" value={formatCount(people.length)} footnote="staff with closed work in scope" />
        <Metric compact label="Total leave" value={formatDecimal(people.reduce((sum, person) => sum + person.leaveDays, 0))} unit="days" />
      </div>
      {people.length === 0 ? (
        <EmptyState title="No leave-adjusted data" hint="Approved leave and closed tasks will appear here." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Person</th>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Leave days</th>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Available days</th>
                <th style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>Closed/day</th>
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <tr key={person.userId} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>{person.name}</td>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>{formatCount(person.leaveDays)} leave days</td>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>{formatCount(person.availableDays)}</td>
                  <td style={{ padding: 'var(--o-space-2) var(--o-space-3)' }}>
                    {person.closedPerAvailableDay === null ? '—' : formatDecimal(person.closedPerAvailableDay)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PanelShell>
  )
}
